// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure rules behind the Home peek (click a Sorted row, read the last
// few messages and answer without leaving Home): which shape it takes, where the
// lifted card sits over its row, and which row Next goes to.

import type {HomeFeedSource} from '@app/features/konvo_today/utils/HomeFeedModel';
import type React from 'react';

export type HomePeekKind = 'channel' | 'whatsapp' | 'mail' | 'clickup';

export type HomePeekTarget =
	| {kind: 'channel'; channelId: string; guildId?: string}
	| {kind: 'whatsapp'; jid: string}
	| {kind: 'mail'; messageId: string}
	/** From a Home row the ClickUp notice is known, and opening the peek reads it. */
	| {kind: 'clickup'; taskId: string; notificationId?: string; read?: boolean};

export interface HomePeekItem {
	key: string;
	name: string;
	/** The row's one-line preview (a ClickUp notice says what happened). */
	preview: string;
	source: HomeFeedSource;
	group: boolean;
	target: HomePeekTarget;
	avatar: (size: 40 | 32 | 24 | 22) => React.ReactNode;
	badge: React.ReactNode;
	/** Under the name instead of the source line (a server channel shows its server). */
	subtitle?: string;
	/** The message the peek is about (what the assistant quoted): marked in the thread. */
	focusMessageId?: string;
	/** What the assistant read, shown when that message is older than the peek's window. */
	snippet?: string;
	/** Go to the real conversation. */
	open: () => void;
	/**
	 * Open it beside Home instead (Home side by side), from the pop-up's Expand. False when it
	 * cannot, and Expand goes to the real conversation.
	 */
	openBeside?: () => boolean;
	/** Put the row away (mark read, hide from Home until something new arrives). Home rows only. */
	done?: () => void;
}

/**
 * card: the row lifts into a card over Home (no longer the desktop default; kept for hosts
 * that place it themselves).
 * side: a panel on the right. Every pop-up on the desktop, like mail (JP 2026-10-04).
 * sheet: a bottom sheet on the phone, for everything.
 * deck: inside a focus deck card (the deck draws the header and the buttons).
 * pane: beside Home's list (Home side by side), for mail and ClickUp, which have no chat view.
 */
export type HomePeekPresentation = 'card' | 'side' | 'sheet' | 'deck' | 'pane';

export function peekPresentation(kind: HomePeekKind, phone: boolean): HomePeekPresentation {
	if (phone) return 'sheet';
	void kind;
	return 'side';
}

export interface PeekRect {
	top: number;
	left: number;
	width: number;
	height: number;
}

const CARD_MIN_WIDTH = 460;
const CARD_MAX_WIDTH = 640;
const CARD_MAX_HEIGHT = 560;
/** The card starts a little above its row, so the row reads as lifted. */
const LIFT = 6;
const MARGIN = 16;

/**
 * Where the lifted card goes: over its row, kept inside the window. `height` is
 * the tallest it may grow; `measured` is how tall it actually is, once known.
 */
export function cardPlacement(
	row: PeekRect,
	viewport: {width: number; height: number},
	measured: number | null = null,
): PeekRect {
	const width = Math.min(
		CARD_MAX_WIDTH,
		Math.max(CARD_MIN_WIDTH, Math.round(row.width * 0.76)),
		viewport.width - MARGIN * 2,
	);
	const height = Math.min(CARD_MAX_HEIGHT, viewport.height - MARGIN * 2);
	// Centred on its row (the Home column), at the row's height, so it reads as that row lifted.
	const centred = row.left + (row.width - width) / 2;
	const left = Math.round(Math.max(MARGIN, Math.min(centred, viewport.width - MARGIN - width)));
	const actual = Math.min(measured ?? height, height);
	const top = Math.max(MARGIN, Math.min(row.top - LIFT, viewport.height - MARGIN - actual));
	return {top, left, width, height};
}

/**
 * Next goes to the next row that was waiting on you when the peek opened and
 * that you have not looked at yet; with none left, simply the next row.
 */
export function nextPeekKey(
	order: ReadonlyArray<string>,
	current: string,
	waiting: ReadonlySet<string>,
	seen: ReadonlySet<string>,
): string | null {
	const index = order.indexOf(current);
	const after = order.slice(index + 1);
	return after.find((key) => waiting.has(key) && !seen.has(key)) ?? after[0] ?? null;
}

/** One step up or down the list, for the side panel's arrows and J/K. */
export function stepPeekKey(order: ReadonlyArray<string>, current: string, delta: 1 | -1): string | null {
	const index = order.indexOf(current);
	if (index === -1) return null;
	return order[index + delta] ?? null;
}

/** Plain text of an email body, for the reply suggestions. */
export function mailPlainText(body: string, html: boolean): string {
	if (!html) return body.replace(/\s+/g, ' ').trim();
	return body
		.replace(/<(style|script|head)[\s\S]*?<\/\1>/gi, ' ')
		.replace(/<br\s*\/?>|<\/(p|div|li|tr|h\d)>/gi, '\n')
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/[ \t\r\f\v]+/g, ' ')
		.replace(/\s*\n\s*/g, '\n')
		.replace(/\n{2,}/g, '\n')
		.trim();
}

/** The reply box text as Outlook-looking HTML. */
export function plainTextToMailHtml(text: string): string {
	const escaped = text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/\n/g, '<br>');
	return `<div style="font-family:Aptos,Calibri,'Segoe UI',Arial,sans-serif;font-size:11pt;line-height:1.4">${escaped}</div>`;
}
